import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { DOCUMENTS_ROUTING_PARAMS, DocumentsPaths } from 'app/configs/routesConfig/documents';
import { FileRequestPaths } from 'app/configs/routesConfig/fileRequest';
import { LoansIframePaths } from 'app/configs/routesConfig/loansIframe';
import {
  ApplicationStatus,
  LoanApplicationTabId,
  loanSubtabsToUpdateOnSaveChanges,
  loanTabsToUpdateOnSaveChanges,
} from 'app/pages/loanApplication/LoanApplication.constants';
import {
  checkIsApplicationAtLeadStage,
  handleNotAcceptableResponse,
} from 'app/pages/loanApplication/LoanApplication.utils';
import NewLeadModal from 'app/pages/loanApplication/tabs/lead/NewLeadModal';
import ChooseProgramPopup from 'app/pages/loanApplication/Validation/ChooseProgramPopup';
import {
  loanActionApiV2,
  loanApplicationBorrowersApi,
  loanApplicationNonBorrowersApi,
} from 'app/services/api';
import DocumentView from 'app/shared/components/document/DocumentView';
import { getGeneratePdfSourceTypeBodyToSend } from 'app/shared/components/loanManagement/loanActionsGroup/LoanActionTransformationHelpers.utils';
import { useRequestCicReasons } from 'app/shared/modules/CicSaveReasonModal/hooks/useRequestCicReasons';
import dayjs from 'dayjs';
import _ from 'lodash';
import { nanoid } from 'nanoid';

import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import {
  AppraisalOperationType,
  CreditReportSubjectType,
  DenialReasonField,
  FloodElevationCertificateProviderType,
  FloodServiceKind,
  LoanActionRequestPayloadType,
  LoanActionResult,
  LoanEditorActionsEnum,
  LoanExportStatus,
  LoanOptionsRequestSource,
  MERS_PREVIEW_MUST_RUN_ALONE_MESSAGE,
  MERS_PROMPT_MUST_RUN_ALONE_MESSAGE,
  normalizeCreditBureaus,
  ReportTypeEnum,
} from 'constants/loanActionEditor';
import { DocumentsSubtabEnum, PricingStatusConfigurationType } from 'constants/loanApplication';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import usePrevious from 'hooks/usePrevious';
import { getDefaultErrorMessage } from 'utils/axios';
import { getConditionalObject } from 'utils/data';
import { buildFormDeliveryDateWindow } from 'utils/date';
import { resolveIframeUrl } from 'utils/iframeUrlTemplate';
import {
  findMersPreviewConflictIndex,
  findMersPromptConflictIndex,
  findMersPreviewResult,
  getMersPromptItem,
  isMersDeactivationPromptAtExecution,
  isMersTransferPromptAtExecution,
  isSnapdocsCommentPromptAtExecution,
  isSnapdocsOptOutReasonPromptAtExecution,
} from 'utils/loanActions';
import { getBorrowersOptions, getNonBorrowerEntitiesOptions } from 'utils/loanApplication';
import { downloadOrShareBlob } from 'utils/nativeFile';
import { openURLInNewTab } from 'utils/navigation';
import { openPdfFromBytes } from 'utils/pdf';
import { buildPendingChangesAuthorsRequest } from 'utils/pendingChangesAuthors';
import { getIsNativePlatform } from 'utils/platform';

import { getUuid, selectMasterCompanyId } from 'store/auth/selectors';
import {
  createDocumentAction,
  getDocumentsAction,
  getDocumentsSilentAction,
} from 'store/documents/actions';
import {
  closeDialogAction,
  openDialogAction,
  showErrorMessageAction,
  showMessageAction,
} from 'store/global/actions';
import { selectDialogs } from 'store/global/selectors';
import {
  editLoanAndLockSummaryAction,
  getAusSubmitOperationsAction,
  getLoanActionsForLoanAction,
  getLoanApplicationCommonDataByApplicationIdAction,
  getLoanApplicationHeaderViewAction,
  getLoanAppraisalSurchargesAction,
  getLoanApplicationTasksAction,
  markAusSubmitOperationStartedAction,
  remountActiveTabAction,
  updatePricingStatusAction,
} from 'store/loanApplications/actions';
import { loadCustomFieldValuesAction } from 'store/loanApplications/actions/customFieldValues.actions';
import { getLeadDataByApplicationAction } from 'store/loanApplications/actions/lead.actions';
import {
  LOAN_APPLICATION_ACTION_TYPE,
  showEscrowRequiredChangesMessageAction,
} from 'store/loanApplications/actions/loanApplication.actions';
import {
  deleteLoanChangesHistoryAction,
  getApplicationChangesHistoryAction,
} from 'store/loanApplications/actions/loanChangesHistory.actions';
import { selectLoanActions } from 'store/loanApplications/selectors';

import { getBlockingSaveMessage } from '../../../../LoanHeaders.utils';
import AppraisalRevisionForm from '../../../AppraisalRevisionForm';
import ArgyleIncomeVerificationForm from '../../../ArgyleIncomeVerificationForm';
import FeeEscalationsForm from '../../../FeeEscalationsForm';
import FloodOrderOptionsForm from '../../../FloodOrderOptionsForm';
import FormPreviewModal from '../../../FormPreviewModal';
import GeneratePdfSourceTypeForm from '../../../GeneratePdfSourceTypeForm';
import HalcyonAddFormsForm from '../../../HalcyonAddFormsForm';
import HalcyonTaxTranscriptForm from '../../../HalcyonTaxTranscriptForm';
import LoanActionModal, {
  FannieLenderForm,
  LoanRoleUserAssignmentForm,
  ResetAusFindings,
} from '../../../LoanActionModal';
import { SendDisclosureType } from '../../../LoanInfoHeader.constants';
import MersDeactivationDetailsForm from '../../../MersDeactivationDetailsForm';
import MersTransferDetailsForm from '../../../MersTransferDetailsForm';
import ReasonForm from '../../../ReasonForm';
import { ReasonFormTestIds } from '../../../ReasonForm.testIds';
import SendDisclosuresForm from '../../../SendDisclosuresForm';
import UploadFormDeliveryForm from '../../../UploadFormDeliveryForm';
import { AssociateReportForm, ConfirmationPreSubmitModal } from '../../Modals';
import EnterCustomFieldsModal from '../../Modals/EnterCustomFieldsModal';
import ImportMismoIntoLoanModal from '../../Modals/ImportMismoIntoLoanModal';
import { useLoanEditorRunnerContext } from '../useLoanEditorRunnerContext';

import {
  actionReportTypes,
  associateReportType,
  createReportType,
  CREDIT_REPORT_DOCUMENT_MAX_RETRIES,
  CREDIT_REPORT_DOCUMENT_RETRY_DELAY_MS,
  getCreditReportActionTypeFromLoanActionType,
  REDIRECT_DELAY_MS,
  NO_ORDER_PAGE_MESSAGE,
  REGGORA_IFRAME_DRAWER_TITLE,
  REGGORA_NO_APPRAISAL_MESSAGE,
  reportActions,
  updateReportType,
} from './constants';
import {
  executeManualLoanAction,
  getBureausOverrideMetadata,
  getCreditReportSubjectIds,
  getLoanActionsAvailability,
  getMessageOptionsOnCreditReportSubmit,
  getRunnerDialogIds,
  HELD_RELOAD_DELAY_MS,
  isHeldForFeeReason,
} from './utils';

/**
 * The loan-action run flow: resolves an action's items into the matching prompt / drawer /
 * download branch and executes it. The loan header uses the defaults (store-loaded actions,
 * manual execute endpoint, loan-editor context); other hosts inject their own.
 * @param {Function} setIsLoading
 * @param {{
 *   actionSource?: Array|null,
 *   executor?: Function,
 *   runnerContext?: object|null,
 *   onAfterExecute?: Function|null,
 *   onClientOnlyLaunch?: Function|null,
 * }} options `actionSource` replaces the store-loaded loan actions and keeps the host's open
 * dialogs alive; `onAfterExecute` replaces the post-execute page reload; `onClientOnlyLaunch`
 * fires when a browser-only branch (custom form / iframe / worksheet / program selection) opens.
 */
export const useLoanActionsControls = (
  setIsLoading,
  {
    actionSource = null,
    executor = executeManualLoanAction,
    runnerContext = null,
    onAfterExecute = null,
    onClientOnlyLaunch = null,
  } = {},
) => {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const loanEditorRunnerContext = useLoanEditorRunnerContext();
  const {
    isLoanEditor,
    applicationId,
    loanId,
    subTab,
    pathBeforeInitialRedirect,
    searchParams,
    borrowerGroups,
    nonBorrowerEntitiesGroups,
    primaryBorrowerId,
    isSaveProgramPermissionsLack,
    isAusSubmitting,
    isAusSummaryExist,
    ausSummary,
    isPendingChangesLoaded,
    isPendingChanges,
    isOptimalBlueEngine,
    applicationStatus,
    loanNumber,
    pricingEngineType,
  } = runnerContext ?? loanEditorRunnerContext;
  const firstLoad = useRef(true);
  const hostDialogIdsRef = useRef(new Set());
  const onExecuteActionsRef = useRef(null);
  const isConvertLeadLoadingRef = useRef(false);

  const storeLoanActions = useSelector(selectLoanActions);
  const isPendingChangesPrevious = usePrevious(isPendingChanges);
  const masterCompanyId = useSelector(selectMasterCompanyId);
  const currentUserId = useSelector(getUuid);

  const loanActions = actionSource ?? storeLoanActions;
  const isStoreActionSource = !actionSource;

  const requestCicReasons = useRequestCicReasons();

  const isAsyncFileRequestEnabled = useFeatureToggle(FeatureFlag.EnableAsyncFileRequest);
  const isPendingChangesByUserEnabled = useFeatureToggle(FeatureFlag.PendingChangesByUser);

  const [executingActionId, setExecutingActionId] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(!isStoreActionSource);
  const { disabledLoanActionsSet, runningLoanActionsSet } = useMemo(
    () => getLoanActionsAvailability(loanActions, { isAusSummaryExist, isAusSubmitting }),
    [loanActions, isAusSummaryExist, isAusSubmitting],
  );
  const [loanEditorCustomFormDrawerState, setLoanEditorCustomFormDrawerState] = useState(null);
  const [iframeDrawerState, setIframeDrawerState] = useState(null);
  const [worksheetDrawerState, setWorksheetDrawerState] = useState(null);
  const [mersPreviewDrawerState, setMersPreviewDrawerState] = useState(null);

  const onCloseLoanEditorCustomFormDrawer = useCallback(
    () => setLoanEditorCustomFormDrawerState(null),
    [],
  );

  const onCloseIframeDrawer = useCallback(() => setIframeDrawerState(null), []);

  const onCloseWorksheetDrawer = useCallback(() => setWorksheetDrawerState(null), []);

  // Closing keeps the preview, so the drawer slides out showing it rather than an empty preview.
  const onCloseMersPreviewDrawer = useCallback(
    () => setMersPreviewDrawerState(state => state && { ...state, isOpen: false }),
    [],
  );

  const closeRunnerDialogs = useCallback(() => {
    if (isStoreActionSource) {
      dispatch(closeDialogAction());

      return;
    }

    getRunnerDialogIds({
      dialogs: selectDialogs(store.getState()),
      hostDialogIds: hostDialogIdsRef.current,
    }).forEach(id => dispatch(closeDialogAction(id)));
  }, [dispatch, isStoreActionSource, store]);

  const refreshLoanApplicationState = useCallback(() => {
    if (!applicationId || !isLoanEditor) {
      return;
    }

    dispatch(getLoanApplicationHeaderViewAction({ applicationId }));
    dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId }));
    dispatch(loadCustomFieldValuesAction({ applicationId }));
  }, [applicationId, dispatch, isLoanEditor]);

  const isDocumentsSubTab = subTab === DocumentsSubtabEnum.DOCUMENTS;
  const isInitiatedByLeadStage = checkIsApplicationAtLeadStage(applicationStatus);

  const entitiesWithRefNumberOptions = useMemo(
    () => [
      ...getBorrowersOptions(borrowerGroups[1] || []),
      ...getNonBorrowerEntitiesOptions(nonBorrowerEntitiesGroups[1] || []),
    ],
    [borrowerGroups, nonBorrowerEntitiesGroups],
  );
  const allEntitiesOptions = useMemo(
    () => [
      ...getBorrowersOptions(borrowerGroups.flat()),
      ...getNonBorrowerEntitiesOptions(nonBorrowerEntitiesGroups.flat()),
    ],
    [borrowerGroups, nonBorrowerEntitiesGroups],
  );
  const allBorrowersOptions = useMemo(
    () => getBorrowersOptions(borrowerGroups.flat()),
    [borrowerGroups],
  );

  const getLoanActions = useCallback(() => {
    if (!applicationId || !isStoreActionSource) {
      return;
    }

    setIsLoading(true);

    dispatch(getLoanActionsForLoanAction({ applicationId }))
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
        setIsDataLoaded(true);
      });
  }, [applicationId, dispatch, isStoreActionSource, setIsLoading, setIsDataLoaded]);

  useEffectSafeStrictMode(() => {
    getLoanActions();
  }, [getLoanActions]);

  useEffectSafeStrictMode(() => {
    if (firstLoad.current && isPendingChangesLoaded) {
      firstLoad.current = false;

      return;
    }

    if (!isPendingChangesPrevious && isPendingChanges) {
      getLoanActions();
    }
  }, [
    firstLoad,
    getLoanActions,
    isPendingChanges,
    isPendingChangesLoaded,
    isPendingChangesPrevious,
  ]);

  const onOpenDocumentInNewTab = useCallback(
    docId => {
      const href = history.createHref({
        pathname: DocumentsPaths.documentPreview.generatePath({
          [DOCUMENTS_ROUTING_PARAMS.docId]: docId,
        }),
      });

      openURLInNewTab(href);
    },
    [history],
  );

  const handleFailedSavePendingChanges = useCallback(
    ({ actionData }) => {
      const onAccept = async props => {
        const { saveWithoutProgram, loanOptionSelector } = props;

        const payload = {
          applicationId,
          loanActionId: actionData.id,
          metadata: {
            ...getConditionalObject(saveWithoutProgram, {
              saveWithoutProgram,
            }),
            ...getConditionalObject(loanOptionSelector, {
              request: { loanOptionSelector },
            }),
            type: LoanActionRequestPayloadType.SavePendingChanges,
          },
        };

        return onExecuteActionsRef.current(payload);
      };

      return handleNotAcceptableResponse({
        dispatch,
        response: { data: actionData },
        onAcceptFromProps: onAccept,
        isInvalidProgramFromProps: actionData.currentProgramNotValid,
        isRetryActionNeededFromProps: !actionData.currentProgramNotValid,
        isSuccessActionNeededAfterAccept: false,
        actionData,
        isSaveWithoutProgramNeededOnNoPrograms: true,
      });
    },
    [applicationId, dispatch],
  );

  const handleFailedLockLoan = useCallback(
    ({ actionData }) => {
      const onAccept = async ({ loanOptionSelector }) => {
        const payload = {
          applicationId,
          loanActionId: actionData.id,
          metadata: {
            ...getConditionalObject(loanOptionSelector, {
              request: { loanOptionSelector },
            }),
            type: LoanActionRequestPayloadType.LockLoan,
          },
        };

        return onExecuteActionsRef.current(payload);
      };

      if (actionData.currentProgramNotValid) {
        const handlerProps = {
          dispatch,
          response: { data: actionData },
          onAcceptFromProps: onAccept,
          isSuccessActionNeededAfterAccept: false,
          actionData,
          loanOptionsSource: LoanOptionsRequestSource.LOCK,
        };

        return dispatch(
          openDialogAction({
            header: 'Confirm Lock Rate',
            size: ModalSize.FULL,
            customButtons: <div className="w-full" id="choose-program-popup-controls" />,
            onAcceptButtonName: 'Change Program',
            hasScrollY: true,
            isWithDivider: true,
            body: () => (
              <ChooseProgramPopup
                isProgramReselection
                handlerProps={handlerProps}
                isRevertNeededAfterAccept={false}
                isSuccessActionNeededAfterAccept={false}
                loanOptionsSource={LoanOptionsRequestSource.LOCK}
                onAccept={onAccept}
                onRevert={_.noop}
              />
            ),
          }),
        );
      }

      return handleNotAcceptableResponse({
        dispatch,
        response: { data: actionData },
        onAcceptFromProps: onAccept,
        isInvalidProgramFromProps: false,
        isRetryActionNeededFromProps: true,
        isSuccessActionNeededAfterAccept: false,
        actionData,
        loanOptionsSource: LoanOptionsRequestSource.LOCK,
      });
    },
    [applicationId, dispatch],
  );

  const openSendDelayedDisclosuresDialog = useCallback(
    delayedCompletionDate => {
      const delayCompletionFormattedDate =
        dayjs(delayedCompletionDate).format(COMMON_DATETIME_FORMAT);
      const title = `Disclosure will be sent on ${delayCompletionFormattedDate}. Would you like to send it now?`;

      dispatch(
        openDialogAction({
          header: 'Send delayed disclosures',
          body: dialogProps => (
            <SendDisclosuresForm
              {...dialogProps}
              disclosureType={SendDisclosureType.DELAYED}
              loanId={loanId}
              title={title}
            />
          ),
        }),
      );
    },
    [dispatch, loanId],
  );

  const onExecuteActions = useCallback(
    (
      payload,
      {
        isRunAusType = false,
        isWithReloading = true,
        isWithPendingChanges = false,
        isSavingPendingChanges = false,
      } = {
        isRunAusType: false,
        isWithReloading: true,
        isWithPendingChanges: false,
        isSavingPendingChanges: false,
      },
    ) => {
      setIsLoading(true);
      setExecutingActionId(payload.loanActionId);

      if (isSavingPendingChanges) {
        dispatch({
          type: LOAN_APPLICATION_ACTION_TYPE.SAVE_LOAN_APPLICATION_CHANGES_PENDING_STATUS,
        });
      }

      return executor(payload)
        .then(async ({ data }) => {
          if (isSavingPendingChanges) {
            dispatch({ type: LOAN_APPLICATION_ACTION_TYPE.SAVE_LOAN_APPLICATION_CHANGES });
          }

          closeRunnerDialogs();

          const { operationId: ausOperationId } =
            data?.find(({ type }) => type === LoanEditorActionsEnum.AUS_REQUEST_STARTED) ?? {};

          if (isRunAusType && ausOperationId) {
            dispatch(
              markAusSubmitOperationStartedAction({
                operationId: ausOperationId,
                loanId,
                companyId: masterCompanyId,
                isPendingChangesRun: isWithPendingChanges,
              }),
            );
          }

          if (isRunAusType && !isWithPendingChanges) {
            dispatch(getAusSubmitOperationsAction({ loanId, companyId: masterCompanyId }));
          }

          const executionSuccessMessages = data?.filter(
            ({ type, message }) => type === LoanActionResult.EXECUTION_SUCCESS && message,
          );

          const requestReportsAction = data?.find(
            ({ type }) => type === LoanActionResult.REQUEST_REPORTS,
          );

          const requestReportsMessage = requestReportsAction?.message;

          const savePendingChangesAction = data?.find(
            ({ type }) => type === LoanEditorActionsEnum.SAVE_PENDING_CHANGES,
          );

          const hasSavePendingChangesAction = !!savePendingChangesAction;

          const iframeAction = data?.find(({ type }) => type === LoanEditorActionsEnum.OPEN_IFRAME);

          const hasIframeAction = !!iframeAction;

          const newTabAction = data?.find(
            ({ type }) => type === LoanEditorActionsEnum.OPEN_URL_IN_NEW_TAB,
          );

          const hasNewTabAction = !!newTabAction;

          const lockLoanAction = data?.find(({ type }) => type === LoanActionResult.LOCK_LOAN);

          // A MERS preview sends and changes nothing: it opens its drawer instead of a toast and the post-action reload.
          const mersPreviewResult = findMersPreviewResult(data);

          const hasMersPreviewResult = !!mersPreviewResult;

          const isFailedResult =
            (!!lockLoanAction && !lockLoanAction.isSuccess) ||
            (hasSavePendingChangesAction && !savePendingChangesAction.isSuccess);

          if (!isFailedResult && !hasMersPreviewResult) {
            onAfterExecute?.(data);
          }

          if (lockLoanAction && !lockLoanAction.isSuccess) {
            return handleFailedLockLoan({
              actionData: { ...lockLoanAction, id: payload.loanActionId },
            });
          }

          if (executionSuccessMessages.length) {
            executionSuccessMessages.forEach(({ message }) => {
              dispatch(showMessageAction({ message, variant: 'success' }));
            });
          }

          if (requestReportsMessage) {
            dispatch(showMessageAction({ message: requestReportsMessage, variant: 'warning' }));
          }

          if (
            !executionSuccessMessages.length &&
            !requestReportsMessage &&
            !hasSavePendingChangesAction &&
            !hasIframeAction &&
            !hasMersPreviewResult
          ) {
            dispatch(
              showMessageAction({
                message: `Loan Action executed successfully`,
                variant: 'success',
              }),
            );
          }

          const impersonationAction = data?.find(
            ({ type }) => type === LoanEditorActionsEnum.IMPERSONATE_AS_BORROWER,
          );

          const downloadMismoFileActionResult = data?.find(
            ({ type }) => type === LoanActionResult.MISMO_FILE_DATA,
          );

          const formsToOpen = data?.reduce((acc, loanAction) => {
            if (loanAction?.type === LoanEditorActionsEnum.GENERATE_FORM) {
              acc.push(...(loanAction?.forms || []));
            }

            if (loanAction?.type === LoanEditorActionsEnum.GENERATE_PDF) {
              acc.push(loanAction);
            }

            return acc;
          }, []);

          const hasFormsToOpen = !!formsToOpen?.length;
          const hasImpersonationAction = !!impersonationAction;
          const hasRequestReportsAction = !!requestReportsAction;
          const hasMismoFileToDownload = !!downloadMismoFileActionResult;

          if (hasMismoFileToDownload) {
            const blob = new Blob([downloadMismoFileActionResult.content]);

            await downloadOrShareBlob(blob, `${downloadMismoFileActionResult.fileName}.xml`);
          }

          if (hasSavePendingChangesAction) {
            const { isSuccess } = savePendingChangesAction;

            if (!isSuccess) {
              return handleFailedSavePendingChanges({
                actionData: { ...savePendingChangesAction, id: payload.loanActionId },
              });
            }

            const { responsePayload } = savePendingChangesAction;

            if (
              responsePayload.metadata?.isSaved === false &&
              responsePayload.metadata?.cicReasonRequired
            ) {
              const request = await requestCicReasons(
                responsePayload.metadata,
                payload.metadata?.request,
              );

              if (!request) {
                return;
              }

              return onExecuteActions(
                {
                  ...payload,
                  metadata: {
                    ...payload.metadata,
                    type: LoanActionRequestPayloadType.SavePendingChanges,
                    request,
                  },
                },
                { isSavingPendingChanges: true },
              );
            }

            const blockingSaveMessage = getBlockingSaveMessage(responsePayload.metadata);

            if (blockingSaveMessage) {
              dispatch(showMessageAction({ message: blockingSaveMessage, variant: 'error' }));

              return;
            }

            batch(() => {
              if (!isInitiatedByLeadStage) {
                dispatch(showEscrowRequiredChangesMessageAction(responsePayload));
              }

              dispatch(deleteLoanChangesHistoryAction());

              // A save by author leaves other people's changes pending, so their history is re-read.
              if (isPendingChangesByUserEnabled && applicationId) {
                dispatch(getApplicationChangesHistoryAction({ applicationId }));
              }

              if (responsePayload.metadata?.isSaved === false && applicationId) {
                dispatch(
                  updatePricingStatusAction({
                    companyId: masterCompanyId,
                    applicationId,
                    pricingEngineType,
                    include: [PricingStatusConfigurationType.PROGRAM_CHANGE_CONFIGURATION],
                  }),
                );
              }

              dispatch({
                type: LOAN_APPLICATION_ACTION_TYPE.SAVE_LOAN_APPLICATION_CHANGES,
                payload: responsePayload,
              });
              dispatch(
                remountActiveTabAction({
                  tabsToRemount: loanTabsToUpdateOnSaveChanges,
                  subtabsToRemount: loanSubtabsToUpdateOnSaveChanges,
                  searchParams,
                  triggeredByPendingChanges: true,
                }),
              );
              refreshLoanApplicationState();
              dispatch(
                showMessageAction({
                  message: `Loan Action executed successfully`,
                  variant: 'success',
                }),
              );
            });

            if (loanId) {
              dispatch(getLoanAppraisalSurchargesAction({ loanId, applicationId }));
            }

            const { delayedCompletionDate } = responsePayload?.loan || {};
            const isApplicationStatusSecondPartCompleted =
              responsePayload?.status === ApplicationStatus.SECOND_PART_COMPLETED;

            if (delayedCompletionDate && !isApplicationStatusSecondPartCompleted) {
              openSendDelayedDisclosuresDialog(delayedCompletionDate);
            }

            return;
          }

          if (hasFormsToOpen) {
            formsToOpen.forEach(({ content }, index) => {
              const isLastElement = index + 1 === formsToOpen?.length;
              openPdfFromBytes(content, {
                shouldFocusTab: isLastElement,
                openInNewTab: true,
              });
            });
          }

          if (hasRequestReportsAction) {
            if (isWithReloading && requestReportsMessage && isLoanEditor) {
              setTimeout(() => {
                dispatch(
                  getDocumentsSilentAction({
                    loanIdOrLegacyLoanNumber: loanId,
                    includeDeleted: true,
                  }),
                );
                refreshLoanApplicationState();
              }, REDIRECT_DELAY_MS);
            }

            const creditAction = requestReportsAction.results?.find(
              ({ type }) => type === ReportTypeEnum.CREDIT,
            );

            if (creditAction) {
              const { value } = creditAction;
              const messageOptions = getMessageOptionsOnCreditReportSubmit({
                response: value.itemsV2,
                actionType: getCreditReportActionTypeFromLoanActionType(payload?.metadata?.type),
              });

              const isReportNotReceivedForSomeEntitiess = messageOptions.variant === 'warning';

              if (!isReportNotReceivedForSomeEntitiess) {
                batch(() => {
                  if (messageOptions.message) {
                    dispatch(showMessageAction(messageOptions));
                  }

                  closeRunnerDialogs();
                });
              }
            }

            const documentId =
              requestReportsAction.results?.find(result => result.documentId)?.documentId ||
              (creditAction?.value?.itemsV2 || []).find(
                item => item.creditReportSubject?.name === CreditReportSubjectType.BORROWER,
              )?.reportRelatedData?.latestCreditReportDocumentId ||
              creditAction?.value?.itemsV2[0]?.reportRelatedData?.latestCreditReportDocumentId;

            const openCreditReportModal = docId =>
              dispatch(
                openDialogAction({
                  header: 'Report',
                  size: ModalSize.XL,
                  body: () => (
                    <DocumentView
                      isOpenInNewTabAllowed
                      isView
                      docId={docId}
                      isControlVisible={false}
                      onOpenDocumentInNewTab={onOpenDocumentInNewTab}
                    />
                  ),
                }),
              );

            if (documentId) {
              return openCreditReportModal(documentId);
            }

            // Fallback: when documentId is not available yet (e.g. first-time credit report request
            // where the async ProcessNewCreditReportCommandHandler may not have committed the
            // document ID to DB before the response was built), retry fetching borrower/NBE data.

            // If there is at least one borrower among NBE's, he will be a priority
            const borrowerId = (creditAction?.value?.itemsV2 || []).find(
              item => item.creditReportSubject?.name === CreditReportSubjectType.BORROWER,
            )?.creditReportSubject?.borrowerId;
            const nonBorrowerEntityId =
              creditAction?.value?.itemsV2[0]?.creditReportSubject?.nonBorrowerEntityId;

            if ((borrowerId || nonBorrowerEntityId) && applicationId) {
              const fetchDocumentIdWithRetry = (
                attemptsRemaining = CREDIT_REPORT_DOCUMENT_MAX_RETRIES,
              ) =>
                new Promise(resolve => {
                  setTimeout(async () => {
                    if (borrowerId) {
                      const { data } = await loanApplicationBorrowersApi.getBorrowerInfo({
                        applicationId,
                        borrowerId,
                      });

                      const freshDocumentId = data?.latestCreditReportDocumentId;

                      if (freshDocumentId) {
                        resolve(freshDocumentId);

                        return;
                      }
                    }

                    if (nonBorrowerEntityId) {
                      const { data } = await loanApplicationNonBorrowersApi.getNonBorrowerData({
                        applicationId,
                        nonBorrowerEntityId,
                      });

                      const freshDocumentId = data?.creditReportData?.latestCreditReportDocumentId;

                      if (freshDocumentId) {
                        resolve(freshDocumentId);

                        return;
                      }
                    }

                    if (attemptsRemaining > 1) {
                      resolve(fetchDocumentIdWithRetry(attemptsRemaining - 1));

                      return;
                    }

                    resolve(null);
                  }, CREDIT_REPORT_DOCUMENT_RETRY_DELAY_MS);
                });

              return fetchDocumentIdWithRetry().then(freshDocumentId => {
                if (freshDocumentId) {
                  openCreditReportModal(freshDocumentId);
                }
              });
            }
          }

          if (hasImpersonationAction) {
            const { confirmationCode, domain, requestId } = impersonationAction;

            const url = `https://${domain}/impersonation/${applicationId}/${requestId}/${confirmationCode}`;
            openURLInNewTab(url, true);
          }

          if (hasMersPreviewResult) {
            setMersPreviewDrawerState({ preview: mersPreviewResult, isOpen: true });
          }

          if (iframeAction?.url) {
            setIframeDrawerState({
              url: iframeAction.url,
              title: REGGORA_IFRAME_DRAWER_TITLE,
            });
          }

          if (iframeAction && !iframeAction.url) {
            dispatch(
              showMessageAction({
                message: iframeAction.emptyMessage ?? REGGORA_NO_APPRAISAL_MESSAGE,
                variant: 'info',
              }),
            );
          }

          if (newTabAction?.url) {
            openURLInNewTab(newTabAction.url, true);
          }

          if (hasNewTabAction && !newTabAction.url) {
            dispatch(
              showMessageAction({
                message: newTabAction.emptyMessage ?? NO_ORDER_PAGE_MESSAGE,
                variant: 'info',
              }),
            );
          }

          // Skip the post-action reload for the actions that navigate or render in place: the Reggora
          // "Open Loan File"/"Open Order Page" iframe-drawer actions (a reload would tear the drawer down)
          // and the HomeVision new-tab "Open Order Page" action (a reload would clear its no-order info
          // toast; the opened tab already carries the user to the order) — same as impersonation.
          if (
            !onAfterExecute &&
            !hasImpersonationAction &&
            !hasIframeAction &&
            !hasNewTabAction &&
            !hasMersPreviewResult
          ) {
            const shouldBackToIframePath =
              LoansIframePaths.loansIframe.matchPath(pathBeforeInitialRedirect);

            // To provide time to show Info toast above
            setTimeout(() => {
              if (shouldBackToIframePath) {
                history.push(pathBeforeInitialRedirect);
              }

              if (isRunAusType) {
                refreshLoanApplicationState();

                if (isDocumentsSubTab) {
                  dispatch(
                    getDocumentsAction({ loanIdOrLegacyLoanNumber: loanId, includeDeleted: true }),
                  );
                }
              }

              if (isWithReloading && !getIsNativePlatform() && !requestReportsMessage) {
                window.location.reload();
              }

              if (isWithReloading && getIsNativePlatform() && !requestReportsMessage) {
                dispatch(
                  getDocumentsAction({ loanIdOrLegacyLoanNumber: loanId, includeDeleted: true }),
                );
              }
            }, REDIRECT_DELAY_MS);
          }

          if (isRunAusType && isWithPendingChanges && ausOperationId) {
            openURLInNewTab(`/loans/${loanId}/operations/${ausOperationId}`, true);
          }
        })
        .catch(err => {
          if (isSavingPendingChanges) {
            dispatch({
              type: LOAN_APPLICATION_ACTION_TYPE.SAVE_LOAN_APPLICATION_CHANGES_REJECTED_STATUS,
            });
          }

          const message = getDefaultErrorMessage(err);

          dispatch(showErrorMessageAction({ message, err }));

          // The change is now pending, so the page has to show it and Save Pending Changes.
          if (isHeldForFeeReason(message)) {
            setTimeout(() => {
              if (getIsNativePlatform()) {
                refreshLoanApplicationState();

                return;
              }

              window.location.reload();
            }, HELD_RELOAD_DELAY_MS);
          }

          return { isExecuteFailed: true };
        })
        .finally(() => {
          setIsLoading(false);
          setExecutingActionId(null);
        });
    },
    [
      setIsLoading,
      dispatch,
      executor,
      closeRunnerDialogs,
      onAfterExecute,
      isLoanEditor,
      handleFailedSavePendingChanges,
      handleFailedLockLoan,
      isInitiatedByLeadStage,
      applicationId,
      masterCompanyId,
      pricingEngineType,
      searchParams,
      openSendDelayedDisclosuresDialog,
      onOpenDocumentInNewTab,
      isDocumentsSubTab,
      history,
      pathBeforeInitialRedirect,
      loanId,
      requestCicReasons,
      refreshLoanApplicationState,
      isPendingChangesByUserEnabled,
    ],
  );

  const getSubmitValues = useCallback(
    ({ actionId, updatedFields }) => {
      const actionData = loanActions.find(({ id }) => id === actionId);

      return {
        applicationId,
        loanActionId: actionData.id,
        metadata: {
          values: updatedFields.reduce((acc, { fieldId, value, denialReasons }) => {
            if (fieldId === DenialReasonField.value) {
              acc[fieldId] = denialReasons;

              return acc;
            }

            acc[fieldId] = value;

            return acc;
          }, {}),
          type: LoanActionRequestPayloadType.FieldDynamicValues,
        },
      };
    },
    [applicationId, loanActions],
  );

  const onFormSubmit = useCallback(
    ({ updatedFields, actionId }) => {
      const payload = getSubmitValues({ actionId, updatedFields });

      return onExecuteActions(payload);
    },
    [onExecuteActions, getSubmitValues],
  );

  const onReportCreateTypeAction = useCallback(
    ({ actionData, reportType }) => {
      const isMultipleOptionss = allEntitiesOptions.length > 1;
      const withBureauSelection = !!reportType?.allowManualSelection;
      const configuredBureaus = normalizeCreditBureaus(reportType?.bureaus);

      const onSubmit = (type, values) => {
        const optionsSource = isMultipleOptionss ? values.options : allEntitiesOptions;
        const { borrowerIds, nonBorrowerEntityIds } = getCreditReportSubjectIds(optionsSource);

        const payload = {
          applicationId,
          loanActionId: actionData.id,
          metadata: {
            type: LoanActionRequestPayloadType.CreditReportCreate,
            borrowerIds,
            nonBorrowerEntityIds,
            hasWrittenBorrowerPermission: values.isWrittenPermission,
            hasVerbalBorrowerPermission: values.isVerbalPermission,
            saveWithoutProgram: isSaveProgramPermissionsLack ? false : undefined,
            ...getBureausOverrideMetadata({ withBureauSelection, values }),
          },
        };

        return onExecuteActions(payload);
      };

      const dialogOptions = {
        header: 'Credit report',
        body: () => (
          <ConfirmationPreSubmitModal
            withPermissionTypeCheck
            buttonAcceptText="Continue"
            buttonDeclineText="Cancel"
            initialBureaus={configuredBureaus}
            message="What type of permission have you obtained from the borrower/NBE to request a credit report?"
            options={allEntitiesOptions}
            selectLabel="Select borrowers/NBE's to run credit report"
            withBureauSelection={withBureauSelection}
            withOptionsSelection={isMultipleOptionss}
            onAccept={onSubmit}
          />
        ),
      };

      dispatch(openDialogAction(dialogOptions));
    },
    [allEntitiesOptions, dispatch, applicationId, isSaveProgramPermissionsLack, onExecuteActions],
  );

  const onReportAssociateTypeAction = useCallback(
    ({ actionData }) => {
      const isMultipleOptions = allEntitiesOptions.length > 1;

      const onSubmit = values => {
        const optionsSource = isMultipleOptions ? values.options : allEntitiesOptions;
        const { borrowerIds, nonBorrowerEntityIds } = getCreditReportSubjectIds(optionsSource);

        const payload = {
          applicationId,
          loanActionId: actionData.id,
          metadata: {
            type: LoanActionRequestPayloadType.CreditReportAssociate,
            borrowerIds,
            nonBorrowerEntityIds,
            // TODO(FH-1450): drop the scalar keys once the SkynetAPI repin is deployed everywhere. They go
            // up alongside the sets so the reissue still reaches the bureau if this bundle is served
            // while the API is on the pre-FH-1450 package, which binds only these.
            ...getConditionalObject(!!borrowerIds.length, { borrowerId: borrowerIds[0] }),
            ...getConditionalObject(!!nonBorrowerEntityIds.length, {
              nonBorrowerEntityId: nonBorrowerEntityIds[0],
            }),
            referenceNumber: values.referenceNumber,
            saveWithoutProgram: isSaveProgramPermissionsLack ? false : undefined,
          },
        };

        return onExecuteActions(payload);
      };

      const dialogOptions = {
        header: 'Associate Report',
        withClosingConfirmation: true,
        body: () => (
          <AssociateReportForm
            options={allEntitiesOptions}
            selectLabel="Select borrowers/NBE's to associate credit report"
            withOptionSelection={isMultipleOptions}
            onSubmitAction={onSubmit}
          />
        ),
      };

      dispatch(openDialogAction(dialogOptions));
    },
    [allEntitiesOptions, applicationId, dispatch, isSaveProgramPermissionsLack, onExecuteActions],
  );

  const onReportUpdateTypeAction = useCallback(
    ({ actionData, reportType }) => {
      const isSingleEntity = entitiesWithRefNumberOptions.length === 1;
      const withBureauSelection = !!reportType?.allowManualSelection;
      const configuredBureaus = normalizeCreditBureaus(reportType?.bureaus);

      const onSubmit = (type, values) => {
        const optionsSource = isSingleEntity ? entitiesWithRefNumberOptions : values.options;
        const borrowerIds = optionsSource.filter(({ isNbe }) => !isNbe).map(({ value }) => value);
        const nonBorrowerEntityIds = optionsSource
          .filter(({ isNbe }) => !!isNbe)
          .map(({ value }) => value);

        const payload = {
          applicationId,
          loanActionId: actionData.id,
          metadata: {
            type: LoanActionRequestPayloadType.CreditReportUpdate,
            borrowerIds,
            nonBorrowerEntityIds,
            saveWithoutProgram: isSaveProgramPermissionsLack ? false : undefined,
            ...getBureausOverrideMetadata({ withBureauSelection, values }),
          },
        };

        return onExecuteActions(payload);
      };

      if (!isSingleEntity || withBureauSelection) {
        const dialogOptions = {
          header: 'Update Credit',
          body: () => (
            <ConfirmationPreSubmitModal
              buttonAcceptText="Continue"
              buttonDeclineText="Cancel"
              initialBureaus={configuredBureaus}
              noChoiceText="Current state of the Loan have no Borrowers/NBE's requiring updates in Credit Report"
              options={entitiesWithRefNumberOptions}
              selectLabel="Select borrowers/NBE's to update credit report"
              withBureauSelection={withBureauSelection}
              withOptionsSelection={!isSingleEntity}
              onAccept={onSubmit}
            />
          ),
        };

        dispatch(openDialogAction(dialogOptions));

        return;
      }

      onSubmit();
    },
    [
      applicationId,
      entitiesWithRefNumberOptions,
      dispatch,
      isSaveProgramPermissionsLack,
      onExecuteActions,
    ],
  );

  const onFinalizeDoAction = useCallback(
    ({ actionData }) => {
      const onSubmit = ({ fannieDoLenderId }) => {
        const payload = {
          applicationId,
          loanActionId: actionData.id,
          metadata: {
            type: LoanEditorActionsEnum.FINALIZE_DO,
            fannieDoLenderId,
          },
        };

        return onExecuteActions(payload);
      };

      const dialogOptions = {
        header: 'Select Fannie Lender',
        body: dialogProps => <FannieLenderForm {...dialogProps} onFormSubmit={onSubmit} />,
      };

      dispatch(openDialogAction(dialogOptions));
    },
    [applicationId, dispatch, onExecuteActions],
  );

  const onSavePendingChangesTypeAction = useCallback(
    ({ actionData, pendingChangesAuthors = null }) => {
      const saveWithoutProgram =
        isInitiatedByLeadStage || (isSaveProgramPermissionsLack ? false : undefined);
      const hasSaveWithoutProgram = _.isBoolean(saveWithoutProgram);
      const resolvedPendingChangesAuthors =
        pendingChangesAuthors ??
        (isPendingChangesByUserEnabled ? buildPendingChangesAuthorsRequest([currentUserId]) : null);
      const hasPendingChangesAuthors = !_.isNil(resolvedPendingChangesAuthors);

      const payload = {
        applicationId,
        loanActionId: actionData.id,
        metadata:
          hasSaveWithoutProgram || hasPendingChangesAuthors
            ? {
                type: LoanActionRequestPayloadType.SavePendingChanges,
                ...getConditionalObject(hasSaveWithoutProgram, { saveWithoutProgram }),
                ...getConditionalObject(hasPendingChangesAuthors, {
                  request: { authors: resolvedPendingChangesAuthors },
                }),
              }
            : null,
      };

      return onExecuteActions(payload, { isSavingPendingChanges: true });
    },
    [
      applicationId,
      onExecuteActions,
      isInitiatedByLeadStage,
      isSaveProgramPermissionsLack,
      isPendingChangesByUserEnabled,
      currentUserId,
    ],
  );

  const onResetAusFindings = useCallback(
    ({ actionData }) => {
      const onSubmit = ausTypeToReset => {
        const payload = {
          applicationId,
          loanActionId: actionData.id,
          metadata: {
            type: LoanEditorActionsEnum.RESET_AUS_FINDINGS,
            ausTypeToReset,
          },
        };

        return onExecuteActions(payload);
      };

      const hasAusResults = ausSummary?.duSummary || ausSummary?.lpSummary;

      if (!hasAusResults) {
        return;
      }

      const ausResults = {
        duStatus: !!ausSummary?.duSummary,
        lpStatus: !!ausSummary?.lpSummary,
      };

      const dialogOptions = {
        header: 'Reset Findings',
        body: dialogProps => (
          <ResetAusFindings {...dialogProps} ausResults={ausResults} onAccept={onSubmit} />
        ),
      };

      dispatch(openDialogAction(dialogOptions));
    },
    [applicationId, ausSummary, dispatch, onExecuteActions],
  );

  const onReportTypeAction = useCallback(
    ({ reportType, actionData }) => {
      switch (reportType.type) {
        case createReportType: {
          onReportCreateTypeAction({ actionData, reportType });

          return;
        }

        case associateReportType: {
          onReportAssociateTypeAction({ actionData });

          return;
        }

        case updateReportType: {
          onReportUpdateTypeAction({ actionData, reportType });

          return;
        }

        default:
          return;
      }
    },
    [onReportAssociateTypeAction, onReportCreateTypeAction, onReportUpdateTypeAction],
  );

  // The worker writes a per-application status blob (building/completed/failed) served by the status
  // endpoint — "failed" is a terminal state with a user-facing message, so a broken build no longer
  // looks like a slow one. A status 404 means the worker has not started yet: keep polling.
  const pollAndDownloadLoanExport = useCallback(() => {
    const pollIntervalMs = 4000;
    const maxAttempts = 45;

    const downloadPackage = async fileName => {
      const { data } = await loanActionApiV2.getLoanExport(applicationId);

      await downloadOrShareBlob(data, fileName ?? `loan-export_${applicationId}.zip`);
      dispatch(
        showMessageAction({ message: 'Loan export package downloaded', variant: 'success' }),
      );
    };

    const poll = async attempt => {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

      try {
        const { data: exportStatus } = await loanActionApiV2.getLoanExportStatus(applicationId);

        if (exportStatus?.status === LoanExportStatus.FAILED) {
          dispatch(
            showErrorMessageAction({
              message:
                exportStatus?.error ?? 'The loan export failed. Try running the action again.',
            }),
          );

          return;
        }

        if (exportStatus?.status === LoanExportStatus.COMPLETED) {
          await downloadPackage(exportStatus?.fileName);

          return;
        }
      } catch (err) {
        if (err?.response?.status !== HTTP_RESPONSE_STATUS_CODES.NOT_FOUND) {
          dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));

          return;
        }
      }

      if (attempt + 1 < maxAttempts) {
        return poll(attempt + 1);
      }

      dispatch(
        showMessageAction({
          message:
            'The loan export is still building. Re-open the loan and run the download again later.',
          variant: 'warning',
        }),
      );
    };

    return poll(0);
  }, [applicationId, dispatch]);

  const onLoanActionClick = useCallback(
    (actionIdOrAction, { pendingChangesAuthors = null } = {}) => {
      const actionData = _.isString(actionIdOrAction)
        ? loanActions.find(({ id }) => id === actionIdOrAction)
        : actionIdOrAction;

      hostDialogIdsRef.current = new Set(selectDialogs(store.getState()).map(({ id }) => id));

      const {
        variableFields,
        reportType,
        finalizeDoType,
        isRunAusType,
        isResetAusFindings,
        isArgyleReportType,
        isHalcyonTaxTranscriptManualSelection,
        isHalcyonPreAuthorization,
        isHalcyonAddFormsManualSelection,
        isGeneratePdf,
        isGeneratePdfTypeHasSourceData,
        isConvertLeadToLoan,
        isAssignLoanRoleUser,
        loanRoleId,
        isWithReloading,
        isWithPendingChanges,
        previewForms,
        isSendFormBundleWithPreview,
        isSavePendingChanges,
        isEnterCustomFields,
        customFieldDefinitionIds,
        openLoanEditorCustomFormId,
        iframe,
        documentWorksheetTypeId,
        documentWorksheetChecklistId,
        isRequestArgyleVerification,
        isRequestFinicityVerification,
        isAppraisalRevisionManualSelection,
        isAppraisalHoldRuntimeReason,
        isPromptForEscalationIdAtExecution,
        isAcceptFeeEscalation,
        isOpenProgramSelection,
        isImportMismoToLoan,
        isExportLoanPackage,
        isUploadFormDeliveryManualSelection,
        uploadFormDeliveryBundleId,
        isFloodOrderPromptAtExecution,
        isServiceLinkFloodOrderManualSelection,
        isSnapdocsCommentPrompt,
        isSnapdocsOptOutReasonPrompt,
        mersDeactivationPromptKind,
        mersTransferPromptKind,
        isMersTransferFlow,
      } = (actionData.loanActionTypes ?? []).reduce(
        (acc, loanActionType) => {
          const {
            changes,
            type,
            reportTypes,
            sourceType,
            allowUserSelection,
            allowManualSelection,
            loanRoleId,
            withAllPendingChanges,
            customFieldDefinitionIds,
            customFormId,
            url,
            title,
            size,
            operation,
            documentTypeId,
            checklistId,
            formBundleId,
            kind,
            promptAtExecution,
          } = loanActionType;
          // A Preview MERS item asks for what the MERS action it previews asks for.
          const mersPromptItem = getMersPromptItem(loanActionType);

          acc.isWithReloading = true;
          acc.isWithPendingChanges = withAllPendingChanges;

          if (type === LoanEditorActionsEnum.SET_FIELD_DYNAMIC) {
            acc.variableFields.push(...changes);
          }

          if (type === LoanEditorActionsEnum.SAVE_PENDING_CHANGES) {
            acc.isSavePendingChanges = true;
          }

          if (reportActions.includes(type)) {
            acc.isArgyleReportType = (actionData.loanActionTypes ?? []).some(loanActionType =>
              loanActionType.reportTypes?.some(
                reportType => reportType.type === ReportTypeEnum.ARGYLE_VERIFICATION,
              ),
            );

            const halcyonManualSelectionReportType = (actionData.loanActionTypes ?? [])
              .flatMap(loanActionType => loanActionType.reportTypes ?? [])
              .find(
                halcyonReportType =>
                  halcyonReportType.type === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT &&
                  halcyonReportType.allowManualSelection,
              );

            acc.isHalcyonTaxTranscriptManualSelection = !!halcyonManualSelectionReportType;
            acc.isHalcyonPreAuthorization = !!halcyonManualSelectionReportType?.isPreAuthorization;

            const serviceLinkFloodOrderManualSelectionReportType = (
              actionData.loanActionTypes ?? []
            )
              .flatMap(loanActionType => loanActionType.reportTypes ?? [])
              .find(
                floodReportType =>
                  floodReportType.type === ReportTypeEnum.FLOOD_ELEVATION_CERTIFICATE &&
                  floodReportType.provider === FloodElevationCertificateProviderType.SERVICE_LINK &&
                  floodReportType.options?.operation === FloodServiceKind.DETERMINATION &&
                  floodReportType.options?.allowManualSelection,
              );

            acc.isServiceLinkFloodOrderManualSelection =
              !!serviceLinkFloodOrderManualSelectionReportType;

            const reportType = reportTypes.find(({ type }) => actionReportTypes.includes(type));

            acc.reportType = reportType;
          }

          if (type === LoanEditorActionsEnum.FINALIZE_DO) {
            acc.finalizeDoType = true;
          }

          if (type === LoanEditorActionsEnum.ASSIGN_LOAN_ROLE && allowUserSelection) {
            acc.isAssignLoanRoleUser = true;
            acc.loanRoleId = loanRoleId;
          }

          if (type === LoanEditorActionsEnum.RUN_AUS) {
            acc.isRunAusType = true;
            acc.isWithReloading = false;
          }

          if (type === LoanEditorActionsEnum.RESET_AUS_FINDINGS) {
            acc.isResetAusFindings = true;
          }

          if (type === LoanEditorActionsEnum.GENERATE_PDF) {
            acc.isGeneratePdf = true;
            acc.isGeneratePdfTypeHasSourceData = !!sourceType;
          }

          if (type === LoanEditorActionsEnum.EXPORT_LOAN_PACKAGE) {
            acc.isExportLoanPackage = true;
          }

          if (type === LoanEditorActionsEnum.CONVERT_LEAD_TO_LOAN) {
            acc.isConvertLeadToLoan = true;
          }

          if (type === LoanEditorActionsEnum.SEND_FORM_BUNDLE) {
            const sendFormBundleActions = (actionData.loanActionTypes ?? []).filter(
              lt => lt.type === LoanEditorActionsEnum.SEND_FORM_BUNDLE,
            );

            const formBundlesWithPreview = sendFormBundleActions.reduce((acc, lt) => {
              const previewFormBundles =
                lt.formBundles?.filter(fb => fb.previewType?.type === 'Form') ?? [];

              acc.push(...previewFormBundles);

              return acc;
            }, []);

            acc.previewForms =
              formBundlesWithPreview.map(fb => ({
                formId: fb.previewType?.formId,
              })) || [];

            acc.isSendFormBundleWithPreview = acc.previewForms.length > 0;
          }

          if (type === LoanEditorActionsEnum.LOCK_LOAN && isOptimalBlueEngine) {
            acc.isWithReloading = false;
          }

          if (type === LoanEditorActionsEnum.ENTER_CUSTOM_FIELDS) {
            acc.isEnterCustomFields = true;
            acc.customFieldDefinitionIds = customFieldDefinitionIds ?? [];
          }

          if (type === LoanEditorActionsEnum.OPEN_LOAN_EDITOR_FORM) {
            acc.openLoanEditorCustomFormId = customFormId ?? null;
          }

          if (type === LoanEditorActionsEnum.OPEN_URL_IN_IFRAME) {
            acc.iframe = { url, title: title ?? null, size: size ?? null };
          }

          if (type === LoanEditorActionsEnum.OPEN_DOCUMENT_WORKSHEET) {
            acc.documentWorksheetTypeId = documentTypeId ?? null;
            acc.documentWorksheetChecklistId = checklistId ?? null;
          }

          if (type === LoanEditorActionsEnum.OPEN_PROGRAM_SELECTION) {
            acc.isOpenProgramSelection = true;
          }

          if (type === LoanEditorActionsEnum.IMPORT_MISMO_TO_LOAN) {
            acc.isImportMismoToLoan = true;
          }

          if (type === LoanEditorActionsEnum.REQUEST_ARGYLE_VERIFICATION) {
            acc.isRequestArgyleVerification = true;
          }

          if (type === LoanEditorActionsEnum.REQUEST_FINICITY_VERIFICATION) {
            acc.isRequestFinicityVerification = true;
          }

          if (type === LoanEditorActionsEnum.ADD_HALCYON_TRANSCRIPT_FORMS && allowManualSelection) {
            acc.isHalcyonAddFormsManualSelection = true;
          }

          if (type === LoanEditorActionsEnum.UPLOAD_FORM_DELIVERY) {
            acc.isUploadFormDeliveryManualSelection = true;
            acc.uploadFormDeliveryBundleId = formBundleId;
          }

          if (
            type === LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE &&
            operation?.type === AppraisalOperationType.REQUEST_REVISION &&
            operation?.allowManualSelection
          ) {
            acc.isAppraisalRevisionManualSelection = true;
          }

          if (
            type === LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE &&
            operation?.type === AppraisalOperationType.HOLD &&
            operation?.promptForReasonAtExecution
          ) {
            acc.isAppraisalHoldRuntimeReason = true;
          }

          if (
            type === LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE &&
            [
              AppraisalOperationType.ACCEPT_FEE_ESCALATION,
              AppraisalOperationType.DENY_FEE_ESCALATION,
            ].includes(operation?.type) &&
            operation?.promptForEscalationIdAtExecution
          ) {
            acc.isPromptForEscalationIdAtExecution = true;
            acc.isAcceptFeeEscalation =
              operation?.type === AppraisalOperationType.ACCEPT_FEE_ESCALATION;
          }

          if (
            type === LoanEditorActionsEnum.RUN_FLOOD_SERVICE &&
            kind === FloodServiceKind.DETERMINATION &&
            promptAtExecution
          ) {
            acc.isFloodOrderPromptAtExecution = true;
          }

          if (isSnapdocsCommentPromptAtExecution(loanActionType)) {
            acc.isSnapdocsCommentPrompt = true;
          }

          if (isMersDeactivationPromptAtExecution(mersPromptItem)) {
            acc.mersDeactivationPromptKind = mersPromptItem.kind;
          }

          if (isMersTransferPromptAtExecution(mersPromptItem)) {
            acc.mersTransferPromptKind = mersPromptItem.kind;
            acc.isMersTransferFlow = !!mersPromptItem.flow;
          }

          if (isSnapdocsOptOutReasonPromptAtExecution(loanActionType)) {
            acc.isSnapdocsOptOutReasonPrompt = true;
          }

          return acc;
        },
        {
          variableFields: [],
          reportType: null,
        },
      );

      // Preview MERS shows what one MERS action would send, so it runs alone. The editor and LoanProcessing refuse to save
      // it beside other items; a task type action can still carry it.

      if (findMersPreviewConflictIndex(actionData.loanActionTypes) !== -1) {
        return dispatch(
          showMessageAction({ message: MERS_PREVIEW_MUST_RUN_ALONE_MESSAGE, variant: 'error' }),
        );
      }

      // A prompting MERS item beside an item that can prompt can never run (one prompt, one set of details per run). The
      // editor and LoanProcessing refuse to save it; an action saved before that, or a task type action, can still carry it.
      if (findMersPromptConflictIndex(actionData.loanActionTypes) !== -1) {
        return dispatch(
          showMessageAction({ message: MERS_PROMPT_MUST_RUN_ALONE_MESSAGE, variant: 'error' }),
        );
      }

      if (reportType) {
        return onReportTypeAction({ reportType, actionData });
      }

      if (isSavePendingChanges) {
        return onSavePendingChangesTypeAction({ actionData, pendingChangesAuthors });
      }

      if (finalizeDoType) {
        return onFinalizeDoAction({ actionData });
      }

      if (isResetAusFindings) {
        return onResetAusFindings({ actionData });
      }

      if (openLoanEditorCustomFormId) {
        onClientOnlyLaunch?.(actionData);

        return setLoanEditorCustomFormDrawerState({
          customFormId: openLoanEditorCustomFormId,
          title: actionData.name,
        });
      }

      if (iframe?.url) {
        const resolvedUrl = resolveIframeUrl(iframe.url, {
          applicationId,
          loanNumber,
          loanId,
          companyId: masterCompanyId,
        });

        onClientOnlyLaunch?.(actionData);

        return setIframeDrawerState({
          url: resolvedUrl,
          title: iframe.title || actionData.name,
          size: iframe.size,
        });
      }

      if (documentWorksheetTypeId) {
        onClientOnlyLaunch?.(actionData);

        return setWorksheetDrawerState({
          documentTypeId: documentWorksheetTypeId,
          checklistId: documentWorksheetChecklistId,
          applicationId,
          loanId,
          title: actionData.name,
        });
      }

      if (isOpenProgramSelection) {
        const onAccept = async ({ pricing, loanOptionSelector }) => {
          setIsLoading(true);

          return dispatch(
            editLoanAndLockSummaryAction({
              applicationId,
              loanId,
              ...getConditionalObject(pricing, { pricing }),
              ...getConditionalObject(loanOptionSelector, { loanOptionSelector }),
              requestMetadata: { tabId: LoanApplicationTabId.LOAN },
              silent: true,
            }),
          )
            .then(() => {
              batch(() => {
                closeRunnerDialogs();

                if (isLoanEditor) {
                  dispatch(getLoanApplicationHeaderViewAction({ applicationId }));
                  dispatch(
                    remountActiveTabAction({
                      tabsToRemount: loanTabsToUpdateOnSaveChanges,
                      subtabsToRemount: loanSubtabsToUpdateOnSaveChanges,
                      searchParams,
                      triggeredByPendingChanges: true,
                    }),
                  );
                }

                dispatch(
                  showMessageAction({ message: 'Program/Rate selected', variant: 'success' }),
                );
              });

              getLoanActions();
            })
            .catch(err => {
              dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
            })
            .finally(() => setIsLoading(false));
        };

        const handlerProps = {
          dispatch,
          response: { data: actionData },
          onAcceptFromProps: onAccept,
          isSuccessActionNeededAfterAccept: false,
          actionData,
          loanOptionsSource: LoanOptionsRequestSource.GENERAL,
        };

        onClientOnlyLaunch?.(actionData);

        return dispatch(
          openDialogAction({
            header: 'Select Program/Rate',
            size: ModalSize.FULL,
            customButtons: <div className="w-full" id="choose-program-popup-controls" />,
            onAcceptButtonName: 'Change Program',
            hasScrollY: true,
            isWithDivider: true,
            body: () => (
              <ChooseProgramPopup
                handlerProps={handlerProps}
                isRevertNeededAfterAccept={false}
                isSuccessActionNeededAfterAccept={false}
                loanOptionsSource={LoanOptionsRequestSource.GENERAL}
                onAccept={onAccept}
                onRevert={_.noop}
              />
            ),
          }),
        );
      }

      const hasVariableFields = !!variableFields?.length;

      if (hasVariableFields) {
        return dispatch(
          openDialogAction({
            header: actionData.name,
            fitContentWidth: true,
            body: dialogProps => (
              <LoanActionModal
                {...dialogProps}
                action={actionData}
                actionId={actionData.id}
                variableFields={variableFields}
                onFormSubmit={onFormSubmit}
              />
            ),
          }),
        );
      }

      if (isAssignLoanRoleUser && loanRoleId) {
        const dialogId = nanoid();
        const onSubmit = userId =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { userId, type: LoanActionRequestPayloadType.LoanRoleUser },
          });

        return dispatch(
          openDialogAction({
            id: dialogId,
            isRestyled: true,
            header: 'Loan Role User Assignment',
            size: ModalSize.SMALL,
            hasScrollY: false,
            body: dialogProps => (
              <LoanRoleUserAssignmentForm
                {...dialogProps}
                dialogId={dialogId}
                loanRoleId={loanRoleId}
                onSubmit={onSubmit}
              />
            ),
          }),
        );
      }

      if (isArgyleReportType) {
        const onSubmit = reportType =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { type: 'Argyle', reportType },
          });

        return dispatch(
          openDialogAction({
            header: 'Argyle Income Verification Report',
            body: dialogProps => (
              <ArgyleIncomeVerificationForm {...dialogProps} onSubmit={onSubmit} />
            ),
          }),
        );
      }

      if (isRequestArgyleVerification) {
        const executeOptions = { isWithReloading: false };
        const refreshTasks = () => dispatch(getLoanApplicationTasksAction());

        return onExecuteActions(
          {
            applicationId,
            loanActionId: actionData.id,
            metadata: {
              type: LoanActionRequestPayloadType.RequestArgyleVerification,
              borrowerId: primaryBorrowerId,
            },
          },
          executeOptions,
        ).then(refreshTasks);
      }

      if (isRequestFinicityVerification) {
        const executeOptions = { isWithReloading: false };
        const refreshTasks = () => dispatch(getLoanApplicationTasksAction());

        return onExecuteActions(
          {
            applicationId,
            loanActionId: actionData.id,
            metadata: {
              type: LoanActionRequestPayloadType.RequestFinicityVerification,
              borrowerId: primaryBorrowerId,
            },
          },
          executeOptions,
        ).then(refreshTasks);
      }

      if (isAppraisalRevisionManualSelection) {
        const onSubmit = ({ title, description }) =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: {
              type: LoanActionRequestPayloadType.RequestAppraisalRevision,
              title,
              description,
            },
          });

        return dispatch(
          openDialogAction({
            header: 'Request Appraisal Revision',
            body: dialogProps => <AppraisalRevisionForm {...dialogProps} onSubmit={onSubmit} />,
          }),
        );
      }

      if (isHalcyonTaxTranscriptManualSelection) {
        const onSubmit = halcyonSelections =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { type: 'HalcyonTaxTranscript', ...halcyonSelections },
          });

        return dispatch(
          openDialogAction({
            header: 'Halcyon Tax Transcript',
            body: dialogProps => (
              <HalcyonTaxTranscriptForm
                {...dialogProps}
                borrowerOptions={allBorrowersOptions}
                isPreAuthorization={isHalcyonPreAuthorization}
                onSubmit={onSubmit}
              />
            ),
          }),
        );
      }

      if (isHalcyonAddFormsManualSelection) {
        const onSubmit = halcyonSelections =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { type: 'HalcyonAddForms', ...halcyonSelections },
          });

        return dispatch(
          openDialogAction({
            header: 'Add Halcyon Transcript Forms',
            body: dialogProps => <HalcyonAddFormsForm {...dialogProps} onSubmit={onSubmit} />,
          }),
        );
      }

      if (isUploadFormDeliveryManualSelection) {
        const onSubmit = ({ file, sentDate, eSignedDate, documentTypeId }) => {
          const formData = new FormData();
          formData.append('file', file, file.name);

          const deliveryDates = buildFormDeliveryDateWindow(sentDate, eSignedDate);

          return dispatch(
            createDocumentAction({
              loanIdOrLegacyLoanNumber: loanId,
              documentTypeId,
              applicationId,
              formData,
            }),
          )
            .then(({ id }) =>
              onExecuteActions({
                applicationId,
                loanActionId: actionData.id,
                metadata: {
                  type: LoanEditorActionsEnum.UPLOAD_FORM_DELIVERY,
                  documentId: id,
                  sentDate: deliveryDates.sentDate,
                  eSignedDate: deliveryDates.eSignedDate ?? undefined,
                },
              }),
            )
            .catch(Error);
        };

        return dispatch(
          openDialogAction({
            header: 'Upload Form Delivery',
            body: dialogProps => (
              <UploadFormDeliveryForm
                {...dialogProps}
                formBundleId={uploadFormDeliveryBundleId}
                onSubmit={onSubmit}
              />
            ),
          }),
        );
      }

      if (isAppraisalHoldRuntimeReason) {
        const onSubmit = reason =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { type: LoanActionRequestPayloadType.AppraisalRuntimeReason, reason },
          });

        return dispatch(
          openDialogAction({
            header: 'Reason for placing the order on hold',
            body: dialogProps => (
              <ReasonForm
                {...dialogProps}
                label="Reason"
                submitButtonName="Place on Hold"
                onSubmit={onSubmit}
              />
            ),
          }),
        );
      }

      if (isPromptForEscalationIdAtExecution) {
        const label = isAcceptFeeEscalation ? 'Accept Fee Escalation' : 'Deny Fee Escalation';
        const onSubmit = feeEscalationId =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: {
              type: LoanActionRequestPayloadType.AppraisalRuntimeFeeEscalationSelection,
              feeEscalationId,
            },
          });

        return dispatch(
          openDialogAction({
            header: label,
            body: dialogProps => (
              <FeeEscalationsForm {...dialogProps} label={label} onSubmit={onSubmit} />
            ),
          }),
        );
      }

      if (isFloodOrderPromptAtExecution || isServiceLinkFloodOrderManualSelection) {
        const onSubmit = ({ productKey, rush, comments }) =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: {
              type: LoanActionRequestPayloadType.FloodOrderOptions,
              productKey,
              rush,
              comments,
            },
          });

        return dispatch(
          openDialogAction({
            header: 'Flood Order Options',
            body: dialogProps => <FloodOrderOptionsForm {...dialogProps} onSubmit={onSubmit} />,
          }),
        );
      }

      if (mersDeactivationPromptKind) {
        const onSubmit = ({ liquidationDate }) =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: {
              type: LoanActionRequestPayloadType.MersDeactivationDetails,
              liquidationDate,
            },
          });

        return dispatch(
          openDialogAction({
            header: actionData.name,
            body: dialogProps => (
              <MersDeactivationDetailsForm
                {...dialogProps}
                kind={mersDeactivationPromptKind}
                onSubmit={onSubmit}
              />
            ),
          }),
        );
      }

      if (mersTransferPromptKind) {
        const onSubmit = details =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { type: LoanActionRequestPayloadType.MersTransferDetails, ...details },
          });

        return dispatch(
          openDialogAction({
            header: actionData.name,
            body: dialogProps => (
              <MersTransferDetailsForm
                {...dialogProps}
                flow={isMersTransferFlow}
                kind={mersTransferPromptKind}
                onSubmit={onSubmit}
              />
            ),
          }),
        );
      }

      if (isSnapdocsCommentPrompt) {
        const onSubmit = text =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { type: LoanActionRequestPayloadType.SnapdocsCommentText, text },
          });

        return dispatch(
          openDialogAction({
            header: actionData.name,
            body: dialogProps => (
              <ReasonForm
                {...dialogProps}
                isRequired
                formTestId={ReasonFormTestIds.SNAPDOCS_COMMENT_FORM}
                inputTestId={ReasonFormTestIds.SNAPDOCS_COMMENT_INPUT}
                label="Comment"
                submitButtonName="Post"
                submitButtonTestId={ReasonFormTestIds.SNAPDOCS_COMMENT_SUBMIT_BUTTON}
                onSubmit={onSubmit}
              />
            ),
          }),
        );
      }

      if (isSnapdocsOptOutReasonPrompt) {
        const onSubmit = reason =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { type: LoanActionRequestPayloadType.SnapdocsOptOutReason, reason },
          });

        return dispatch(
          openDialogAction({
            header: actionData.name,
            body: dialogProps => (
              <ReasonForm
                {...dialogProps}
                isRequired
                formTestId={ReasonFormTestIds.SNAPDOCS_OPT_OUT_FORM}
                inputTestId={ReasonFormTestIds.SNAPDOCS_OPT_OUT_REASON_INPUT}
                label="Reason"
                submitButtonName="Opt Out"
                submitButtonTestId={ReasonFormTestIds.SNAPDOCS_OPT_OUT_SUBMIT_BUTTON}
                onSubmit={onSubmit}
              />
            ),
          }),
        );
      }

      if (isGeneratePdf) {
        const dialogId = nanoid();

        const onExecuteAction = async sourceTypeForm => {
          const payload = {
            applicationId,
            loanActionId: actionData.id,
            metadata: sourceTypeForm
              ? {
                  type: LoanEditorActionsEnum.GENERATE_PDF,
                  sourceType: getGeneratePdfSourceTypeBodyToSend(sourceTypeForm),
                }
              : null,
          };

          if (!isGeneratePdfTypeHasSourceData) {
            dispatch(closeDialogAction(dialogId));
          }

          if (!isAsyncFileRequestEnabled) {
            return onExecuteActions(payload);
          }

          setIsLoading(true);

          return executor(payload)
            .then(({ data }) => {
              const fileRequestId = data[0]?.fileRequestId;

              openURLInNewTab(
                FileRequestPaths.fileRequestManagement.generatePath({
                  fileRequestId,
                }),
              );
              onAfterExecute?.(data);
            })
            .finally(() => setIsLoading(false));
        };

        if (!isGeneratePdfTypeHasSourceData) {
          return dispatch(
            openDialogAction({
              id: dialogId,
              header: 'Please select source to generate PDF',
              body: dialogProps => (
                <GeneratePdfSourceTypeForm {...dialogProps} onSubmit={onExecuteAction} />
              ),
            }),
          );
        }

        return onExecuteAction();
      }

      if (isConvertLeadToLoan) {
        const onSubmit = form =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { fields: form, type: LoanEditorActionsEnum.CONVERT_LEAD_TO_LOAN },
          });

        const openConvertLeadDialog = () =>
          dispatch(
            openDialogAction({
              header: 'Convert Lead to Loan',
              isRestyled: true,
              withDivider: true,
              size: ModalSize.XL,
              body: dialogProps => (
                <NewLeadModal {...dialogProps} isConvertLeadToLoan onSubmit={onSubmit} />
              ),
            }),
          );

        if (isConvertLeadLoadingRef.current) {
          return;
        }

        // The form seeds its values from the lead slice once, on mount, so the lead must be loaded
        // before the dialog opens -- the same order the CRM lead page uses.
        isConvertLeadLoadingRef.current = true;
        setIsLoading(true);

        return dispatch(getLeadDataByApplicationAction(applicationId))
          .catch(Error)
          .finally(() => {
            isConvertLeadLoadingRef.current = false;
            setIsLoading(false);
            openConvertLeadDialog();
          });
      }

      if (isEnterCustomFields) {
        const dialogId = nanoid();

        return dispatch(
          openDialogAction({
            id: dialogId,
            header: actionData.name,
            fitContentWidth: true,
            isRestyled: true,
            body: dialogProps => (
              <EnterCustomFieldsModal
                {...dialogProps}
                applicationId={applicationId}
                customFieldDefinitionIds={customFieldDefinitionIds}
                dialogId={dialogId}
                executor={executor}
                loanActionId={actionData.id}
                onSubmitSuccess={onAfterExecute}
              />
            ),
          }),
        );
      }

      if (isImportMismoToLoan) {
        const dialogId = nanoid();

        const onProceed = stagingId =>
          onExecuteActions({
            applicationId,
            loanActionId: actionData.id,
            metadata: { stagingId, type: LoanEditorActionsEnum.IMPORT_MISMO_TO_LOAN },
          });

        return dispatch(
          openDialogAction({
            id: dialogId,
            header: actionData.name,
            isRestyled: true,
            size: ModalSize.LARGE,
            body: dialogProps => (
              <ImportMismoIntoLoanModal
                {...dialogProps}
                applicationId={applicationId}
                dialogId={dialogId}
                onProceed={onProceed}
              />
            ),
          }),
        );
      }

      if (isSendFormBundleWithPreview) {
        const dialogId = nanoid();

        const onExecuteAction = async () => {
          const payload = {
            applicationId,
            loanActionId: actionData.id,
            metadata: null,
          };

          dispatch(closeDialogAction(dialogId));

          return onExecuteActions(payload);
        };

        return dispatch(
          openDialogAction({
            id: dialogId,
            header: 'Preview Form(s)',
            size: ModalSize.XL,
            body: dialogProps => (
              <FormPreviewModal
                {...dialogProps}
                applicationId={applicationId}
                previewForms={previewForms}
                onProceed={onExecuteAction}
              />
            ),
          }),
        );
      }

      const payload = {
        applicationId,
        loanActionId: actionData.id,
        metadata: null,
      };

      if (isExportLoanPackage) {
        // The export is a read-only aggregation, and the post-execute reload would tear down the
        // in-page poll that delivers the download — so opt out of reloading. The loading state is
        // re-armed for the poll window so the action can't be double-fired while a download is pending.
        return onExecuteActions(payload, { isWithReloading: false }).then(result => {
          if (result?.isExecuteFailed) {
            return undefined;
          }

          setIsLoading(true);
          setExecutingActionId(actionData.id);

          return pollAndDownloadLoanExport().finally(() => {
            setIsLoading(false);
            setExecutingActionId(null);
          });
        });
      }

      return onExecuteActions(payload, {
        isRunAusType,
        isWithReloading,
        isWithPendingChanges,
      });
    },
    [
      loanActions,
      store,
      executor,
      closeRunnerDialogs,
      onAfterExecute,
      onClientOnlyLaunch,
      isLoanEditor,
      applicationId,
      pollAndDownloadLoanExport,
      allBorrowersOptions,
      primaryBorrowerId,
      onExecuteActions,
      isOptimalBlueEngine,
      onReportTypeAction,
      onSavePendingChangesTypeAction,
      onFinalizeDoAction,
      onResetAusFindings,
      dispatch,
      onFormSubmit,
      isAsyncFileRequestEnabled,
      setIsLoading,
      loanId,
      loanNumber,
      masterCompanyId,
      searchParams,
      getLoanActions,
    ],
  );

  useEffect(() => {
    onExecuteActionsRef.current = onExecuteActions;
  }, [onExecuteActions]);

  return {
    executingActionId,
    isDataLoaded,
    onLoanActionClick,
    loanActions,
    disabledLoanActionsSet,
    runningLoanActionsSet,
    loanEditorCustomFormDrawerState,
    onCloseLoanEditorCustomFormDrawer,
    iframeDrawerState,
    onCloseIframeDrawer,
    worksheetDrawerState,
    onCloseWorksheetDrawer,
    mersPreviewDrawerState,
    onCloseMersPreviewDrawer,
  };
};
